import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  useToast,
  Divider,
  Alert,
  AlertIcon,
  Checkbox,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tooltip,
  Link,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FaCoins, FaGift, FaInfoCircle, FaCheckCircle, FaCheck, FaHeart, FaArrowUp, FaUsers, FaCopy } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import AcceptJSPaymentForm from './AcceptJSPaymentForm';

// ─── GENEROSITY TIERS (MU pledge, no dollars) ───
const generosityTiers = [
  {
    id: 'base',
    name: 'Base',
    muPledge: 0,
    features: [
      'Comment on videos',
      'Access to public chat',
    ],
    color: 'gray'
  },
  {
    id: 'supporter',
    name: 'Supporter',
    muPledge: 100,
    features: [
      'Comment on videos',
      'Access to public chat',
      'Interruption-free videos',
      'Private chat access',
      'State chat access',
      'Personal profile',
      'Send units to other members',
    ],
    color: 'blue'
  },
  {
    id: 'contributor',
    name: 'Contributor',
    muPledge: 150,
    features: [
      'Comment on videos',
      'Access to public chat',
      'Interruption-free videos',
      'Private chat access',
      'State chat access',
      'Personal profile',
      'Send units to other members',
      'Member recognition badge',
    ],
    popular: true,
    color: 'purple'
  },
  {
    id: 'vip',
    name: 'VIP',
    muPledge: 200,
    features: [
      'Comment on videos',
      'Access to public chat',
      'Interruption-free videos',
      'Private chat access',
      'State chat access',
      'Personal profile',
      'Send units to other members',
      'Member recognition badge',
      'Law education access',
      'Custom icons to choose from',
    ],
    color: 'yellow'
  }
];

// ─── MONTHLY CONTRIBUTION TIERS (dollars → generates MU) ───
const monthlyContributionTiers = [
  {
    id: 'monthly-supporter',
    name: 'Supporter',
    amount: 9.99,
    muGenerated: 100,
    color: 'blue'
  },
  {
    id: 'monthly-contributor',
    name: 'Contributor',
    amount: 14.99,
    muGenerated: 200,
    popular: true,
    color: 'purple'
  },
  {
    id: 'monthly-vip',
    name: 'VIP',
    amount: 19.99,
    muGenerated: 300,
    color: 'yellow'
  }
];

// ─── SINGLE CONTRIBUTION TIERS (one-time dollars → MU) ───
const singleContributionTiers = [
  {
    id: 'single-100',
    name: 'Starter',
    amount: 9.99,
    muGenerated: 100,
    color: 'blue'
  },
  {
    id: 'single-200',
    name: 'Supporter',
    amount: 14.99,
    muGenerated: 200,
    color: 'purple'
  },
  {
    id: 'single-300',
    name: 'Patron',
    amount: 19.99,
    muGenerated: 300,
    popular: true,
    color: 'yellow'
  },
  {
    id: 'single-800',
    name: 'Benefactor',
    amount: 49.99,
    muGenerated: 800,
    color: 'teal'
  },
  {
    id: 'single-1600',
    name: 'Champion',
    amount: 89.99,
    muGenerated: 1600,
    color: 'orange'
  },
  {
    id: 'single-3200',
    name: 'Luminary',
    amount: 169.99,
    muGenerated: 3200,
    color: 'pink'
  }
];

// ═══════════════════════════════════════════════════════════
// MODAL 1: Choose Your Generosity (MU pledge only, no dollars)
// ═══════════════════════════════════════════════════════════
const GenerosityModal = ({ isOpen, onClose, currentTier, onSelectTier }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" maxW="90vw" w="90vw">
        <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
          <VStack align="start" spacing={1}>
            <HStack spacing={3}>
              <Icon as={FaHeart} color="pink.400" boxSize={6} />
              <Text>Choose Your Generosity</Text>
            </HStack>
            <Text fontSize="sm" color="gray.400" fontWeight="normal" pl={9}>
              Your generosity determines your access and how many Member Units your membership pledges each month.
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={8}>
          <HStack spacing={5} align="stretch" justify="center" overflowX="auto" overflow="visible" pt={4} pb={4}>
            {generosityTiers.map(tier => {
              const isCurrent = currentTier === tier.id;
              return (
                <Box
                  key={tier.id}
                  p={5}
                  borderRadius="xl"
                  borderWidth="2px"
                  borderColor={isCurrent ? `${tier.color}.400` : 'gray.600'}
                  bg={isCurrent ? `${tier.color}.900` : 'gray.700'}
                  cursor="pointer"
                  transition="all 0.2s"
                  flex="1"
                  minW="200px"
                  maxW="260px"
                  _hover={{ borderColor: `${tier.color}.400`, transform: 'translateY(-4px)', boxShadow: 'lg' }}
                  position="relative"
                  overflow="visible"
                  onClick={() => {
                    onSelectTier(tier.id);
                    onClose();
                  }}
                  display="flex"
                  flexDirection="column"
                >
                  {tier.popular && (
                    <Badge
                      position="absolute"
                      top="-10px"
                      left="50%"
                      transform="translateX(-50%)"
                      colorScheme="green"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      MOST POPULAR
                    </Badge>
                  )}
                  {isCurrent && (
                    <Badge
                      position="absolute"
                      top="-10px"
                      right="8px"
                      colorScheme="blue"
                      px={2}
                      py={1}
                      borderRadius="full"
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      CURRENT
                    </Badge>
                  )}
                  <VStack align="center" spacing={3} h="full">
                    {/* Tier Name */}
                    <Text fontWeight="bold" fontSize="xl" color={`${tier.color}.200`}>{tier.name}</Text>

                    {/* MU Pledge — Primary Display */}
                    <Box textAlign="center">
                      {tier.muPledge === 0 ? (
                        <VStack spacing={0}>
                          <Text fontSize="3xl" fontWeight="black" lineHeight="1">0 MU</Text>
                          <Text fontSize="xs" color="gray.500" mt={1}>no pledge required</Text>
                        </VStack>
                      ) : (
                        <VStack spacing={0}>
                          <HStack spacing={1} justify="center" align="baseline">
                            <Icon as={FaCoins} color="yellow.400" boxSize={4} />
                            <Text fontSize="3xl" fontWeight="black" lineHeight="1">{tier.muPledge}</Text>
                            <Text fontSize="md" fontWeight="bold" color="gray.400">MU</Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.500" mt={1}>pledged monthly</Text>
                        </VStack>
                      )}
                    </Box>

                    <Divider borderColor="gray.600" />

                    {/* Feature List — Bullet Points */}
                    <List spacing={2} w="full" flex="1" px={1}>
                      {tier.features.map((feature, i) => (
                        <ListItem key={i} display="flex" alignItems="flex-start" fontSize="sm" color="gray.300">
                          <ListIcon as={FaCheck} color={`${tier.color}.400`} mt="4px" flexShrink={0} />
                          <Text>{feature}</Text>
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      colorScheme={tier.color}
                      w="full"
                      size="sm"
                      variant={isCurrent ? "solid" : "outline"}
                      mt="auto"
                    >
                      {isCurrent ? "Current" : "Select"}
                    </Button>
                  </VStack>
                </Box>
              );
            })}
          </HStack>

          {/* Auto-downgrade notice */}
          <Box mt={6} px={4}>
            <Alert status="info" bg="gray.700" borderRadius="md" borderWidth="1px" borderColor="blue.600">
              <AlertIcon color="blue.400" />
              <Text fontSize="xs" color="gray.300">
                If your membership's Member Units are fully depleted, your generosity will be adjusted to Base to ensure fair access for all members.
              </Text>
            </Alert>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════
// MODAL 2: Set Up Monthly Contribution (dollars → generates MU)
// ═══════════════════════════════════════════════════════════
const MonthlyContributionModal = ({ isOpen, onClose, onSelectTier, onSwitchToSingle }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" maxW="90vw" w="90vw">
        <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
          <VStack align="start" spacing={1}>
            <HStack spacing={3}>
              <Icon as={FaGift} color="blue.400" boxSize={6} />
              <Text>Set Up Monthly Contribution</Text>
            </HStack>
            <Text fontSize="sm" color="gray.400" fontWeight="normal" pl={9}>
              A recurring contribution generates Member Units for your membership each month.
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={8}>
          <HStack spacing={5} align="stretch" justify="center" overflowX="auto" overflow="visible" pt={4} pb={4}>
            {monthlyContributionTiers.map(tier => (
              <Box
                key={tier.id}
                p={5}
                borderRadius="xl"
                borderWidth="2px"
                borderColor="gray.600"
                bg="gray.700"
                cursor="pointer"
                transition="all 0.2s"
                flex="1"
                minW="220px"
                maxW="280px"
                _hover={{ borderColor: `${tier.color}.400`, transform: 'translateY(-4px)', boxShadow: 'lg' }}
                position="relative"
                overflow="visible"
                onClick={() => {
                  onSelectTier(tier);
                  onClose();
                }}
                display="flex"
                flexDirection="column"
              >
                {tier.popular && (
                  <Badge
                    position="absolute"
                    top="-10px"
                    left="50%"
                    transform="translateX(-50%)"
                    colorScheme="green"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    MOST POPULAR
                  </Badge>
                )}
                <VStack align="center" spacing={4} h="full">
                  {/* Tier Name */}
                  <Text fontWeight="bold" fontSize="xl" color={`${tier.color}.200`}>{tier.name}</Text>

                  {/* Dollar Amount — Primary */}
                  <VStack spacing={0}>
                    <Text fontSize="3xl" fontWeight="black" lineHeight="1">${tier.amount}</Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>per month</Text>
                  </VStack>

                  <Divider borderColor="gray.600" />

                  {/* MU Generated */}
                  <Box w="full" bg="gray.800" px={3} py={3} borderRadius="md" borderWidth="1px" borderColor={`${tier.color}.500`}>
                    <VStack spacing={1}>
                      <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="semibold">Generates</Text>
                      <HStack justify="center" spacing={2}>
                        <Icon as={FaCoins} color="yellow.400" boxSize={5} />
                        <Text fontSize="2xl" fontWeight="bold" color="yellow.300">+{tier.muGenerated}</Text>
                        <Text fontSize="md" fontWeight="bold" color="gray.400">MU</Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">each month</Text>
                    </VStack>
                  </Box>

                  <Button
                    colorScheme={tier.color}
                    w="full"
                    size="sm"
                    variant="outline"
                    mt="auto"
                  >
                    Select
                  </Button>
                </VStack>
              </Box>
            ))}
          </HStack>

          {/* Single contribution alternative */}
          <Divider borderColor="gray.600" mt={6} />
          <Box textAlign="center" pt={4}>
            <Text fontSize="sm" color="gray.400" mb={2}>Prefer a one-time contribution?</Text>
            <Button
              variant="ghost"
              color="teal.300"
              size="sm"
              leftIcon={<Icon as={FaCoins} />}
              onClick={() => {
                onClose();
                if (onSwitchToSingle) onSwitchToSingle();
              }}
              _hover={{ bg: 'rgba(49, 151, 149, 0.1)' }}
            >
              Make a Single Contribution Instead
            </Button>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
// ═══════════════════════════════════════════════════════════
const SingleContributionModal = ({ isOpen, onClose, onSelectTier, onSwitchToRoutine }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" maxW="90vw" w="90vw">
        <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
          <HStack spacing={3}>
            <Icon as={FaCoins} color="yellow.400" boxSize={6} />
            <Text>Single Contribution</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={8}>
          <HStack spacing={4} align="stretch" justify="center" flexWrap="wrap" overflow="visible" pt={4} pb={4}>
            {singleContributionTiers.map(tier => (
              <Box
                key={tier.id}
                p={4}
                borderRadius="xl"
                borderWidth="2px"
                borderColor="gray.600"
                bg="gray.700"
                cursor="pointer"
                transition="all 0.2s"
                flex="1"
                minW="170px"
                maxW="220px"
                _hover={{ borderColor: `${tier.color}.400`, transform: 'translateY(-4px)', boxShadow: 'lg' }}
                position="relative"
                overflow="visible"
                onClick={() => {
                  onSelectTier(tier);
                  onClose();
                }}
                display="flex"
                flexDirection="column"
              >
                {tier.popular && (
                  <Badge
                    position="absolute"
                    top="-10px"
                    left="50%"
                    transform="translateX(-50%)"
                    colorScheme="green"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    BEST VALUE
                  </Badge>
                )}
                <VStack align="center" spacing={4} h="full">
                  {/* Tier Name */}
                  <Text fontWeight="bold" fontSize="xl" color={`${tier.color}.200`}>{tier.name}</Text>

                  {/* Dollar Amount — Primary */}
                  <Text fontSize="3xl" fontWeight="black" lineHeight="1">${tier.amount}</Text>
                  <Text fontSize="xs" color="gray.500" mt={-2}>one-time</Text>

                  <Divider borderColor="gray.600" />

                  {/* MU Generated */}
                  <Box w="full" bg="gray.800" px={3} py={3} borderRadius="md" borderWidth="1px" borderColor={`${tier.color}.500`}>
                    <VStack spacing={1}>
                      <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="semibold">Generates</Text>
                      <HStack justify="center" spacing={2}>
                        <Icon as={FaCoins} color="yellow.400" boxSize={5} />
                        <Text fontSize="2xl" fontWeight="bold" color="yellow.300">+{tier.muGenerated}</Text>
                        <Text fontSize="md" fontWeight="bold" color="gray.400">MU</Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">added to your balance</Text>
                    </VStack>
                  </Box>

                  <Button
                    colorScheme={tier.color}
                    w="full"
                    size="sm"
                    variant="outline"
                    mt="auto"
                  >
                    Select
                  </Button>
                </VStack>
              </Box>
            ))}
          </HStack>

          {/* Routine contribution alternative */}
          <Divider borderColor="gray.600" mt={6} />
          <Box textAlign="center" pt={4}>
            <Text fontSize="sm" color="gray.400" mb={2}>Prefer a routine monthly contribution?</Text>
            <Button
              variant="ghost"
              color="blue.300"
              size="sm"
              leftIcon={<Icon as={FaGift} />}
              onClick={() => {
                onClose();
                if (onSwitchToRoutine) onSwitchToRoutine();
              }}
              _hover={{ bg: 'rgba(66, 153, 225, 0.1)' }}
            >
              Set Up a Routine Contribution Instead
            </Button>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════
// MODAL 4: Labor Contribution (Referral Program)
// ═══════════════════════════════════════════════════════════
const LaborContributionModal = ({ isOpen, onClose, burrowerId }) => {
  const toast = useToast();

  const handleCopyId = () => {
    if (burrowerId) {
      navigator.clipboard.writeText(burrowerId);
      toast({
        title: 'Burrower ID Copied',
        description: `"${burrowerId}" has been copied to your clipboard.`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
          <HStack spacing={3}>
            <Icon as={FaUsers} color="green.400" boxSize={6} />
            <Text>Labor Contribution</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={8}>
          <VStack spacing={6} align="stretch">
            {/* Hero explanation */}
            <Box bg="green.900" borderWidth="1px" borderColor="green.600" borderRadius="lg" p={5}>
              <Text fontSize="lg" fontWeight="bold" color="green.200" mb={3}>
                Grow the Assembly — Generate Member Units Through Labor
              </Text>
              <Text color="gray.200" lineHeight="tall">
                When you invite someone to join the assembly, the assembly recognizes your labor.
                You receive <Text as="span" fontWeight="bold" color="green.300">20% of the Member Units they generate
                from their first contribution</Text>, plus <Text as="span" fontWeight="bold" color="green.300">5% of all
                contributions they make for the next 12 months</Text>.
              </Text>
            </Box>

            {/* How it works */}
            <Box>
              <Text fontWeight="semibold" fontSize="md" color="gray.300" mb={3}>
                How It Works
              </Text>
              <VStack spacing={3} align="stretch">
                <HStack align="start" spacing={3}>
                  <Badge colorScheme="green" borderRadius="full" px={2.5} py={0.5} mt={0.5} fontSize="sm">1</Badge>
                  <Text color="gray.300">Share your <Text as="span" fontWeight="bold" color="white">Burrower ID</Text> with someone interested in the assembly.</Text>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Badge colorScheme="green" borderRadius="full" px={2.5} py={0.5} mt={0.5} fontSize="sm">2</Badge>
                  <Text color="gray.300">They create their membership and enter your Burrower ID as their referrer.</Text>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Badge colorScheme="green" borderRadius="full" px={2.5} py={0.5} mt={0.5} fontSize="sm">3</Badge>
                  <Text color="gray.300">Their <Text as="span" fontWeight="bold" color="white">first contribution</Text> generates a <Text as="span" fontWeight="bold" color="green.300">20% MU bonus</Text> for you.</Text>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Badge colorScheme="green" borderRadius="full" px={2.5} py={0.5} mt={0.5} fontSize="sm">4</Badge>
                  <Text color="gray.300">Every contribution they make over the <Text as="span" fontWeight="bold" color="white">next 12 months</Text> generates a <Text as="span" fontWeight="bold" color="green.300">5% MU bonus</Text> for you.</Text>
                </HStack>
              </VStack>
            </Box>

            <Divider borderColor="gray.600" />

            {/* Example */}
            <Box bg="gray.700" borderRadius="lg" p={4}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.400" mb={2}>Example</Text>
              <Text color="gray.300" fontSize="sm" mb={2}>
                <Text as="span" fontWeight="bold" color="white">First contribution:</Text> Your referral contributes $49.99 and generates <Text as="span" fontWeight="bold" color="yellow.300">800 MU</Text>.
                You receive <Text as="span" fontWeight="bold" color="green.300">160 MU</Text> (20%).
              </Text>
              <Text color="gray.300" fontSize="sm">
                <Text as="span" fontWeight="bold" color="white">Months 2–12:</Text> They set up a $14.99/mo routine contribution generating <Text as="span" fontWeight="bold" color="yellow.300">200 MU</Text> each month.
                You receive <Text as="span" fontWeight="bold" color="green.300">10 MU</Text> (5%) each month for the remainder of the year.
              </Text>
            </Box>

            <Divider borderColor="gray.600" />

            {/* Burrower ID display + copy */}
            <Box>
              <Text fontSize="sm" color="gray.400" mb={2} textAlign="center">Your Burrower ID</Text>
              <HStack
                bg="gray.700"
                borderWidth="2px"
                borderColor="green.500"
                borderRadius="lg"
                p={4}
                justify="center"
                spacing={4}
              >
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color="green.300"
                  letterSpacing="wider"
                  fontFamily="mono"
                >
                  {burrowerId || '—'}
                </Text>
                <Tooltip label="Copy to clipboard" hasArrow>
                  <Button
                    size="sm"
                    colorScheme="green"
                    variant="ghost"
                    onClick={handleCopyId}
                    leftIcon={<Icon as={FaCopy} />}
                    isDisabled={!burrowerId}
                  >
                    Copy
                  </Button>
                </Tooltip>
              </HStack>
              <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
                Share this ID with anyone you invite to the assembly.
              </Text>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN MODAL: Member Assembly Hub
// ═══════════════════════════════════════════════════════════
const MembershipModal = ({ isOpen, onClose }) => {
  const [isGenerosityModalOpen, setIsGenerosityModalOpen] = useState(false);
  const [isMonthlyModalOpen, setIsMonthlyModalOpen] = useState(false);
  const [isSingleModalOpen, setIsSingleModalOpen] = useState(false);
  const [isLaborModalOpen, setIsLaborModalOpen] = useState(false);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [paymentTierData, setPaymentTierData] = useState(null);
  const [paymentType, setPaymentType] = useState('subscribe');
  const toast = useToast();
  const dispatch = useDispatch();
  const profile = useSelector(state => state.auth.profile);

  // When user selects a generosity tier (MU pledge only, no payment)
  const handleGenerositySelected = (tierId) => {
    if (tierId === 'base') {
      toast({
        title: 'Base Generosity Selected',
        description: 'Your membership pledges 0 MU monthly. No contribution needed.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } else {
      // If member is on Base (no MU coming in), redirect to routine contribution
      const currentTier = profile.membershipTier || 'base';
      if (currentTier === 'base') {
        toast({
          title: `${tierId.charAt(0).toUpperCase() + tierId.slice(1)} Generosity Selected`,
          description: 'Set up a routine contribution to generate the Member Units your generosity will pledge.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        // Open the routine contribution modal
        setIsMonthlyModalOpen(true);
      } else {
        // Already has a contribution flowing — just update the tier
        toast({
          title: 'Generosity Updated',
          description: `Your membership now pledges MU at the ${tierId.charAt(0).toUpperCase() + tierId.slice(1)} generosity.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // When user selects a monthly contribution (dollars → recurring)
  const handleMonthlySelected = (tier) => {
    setPaymentTierData({ ...tier, muAllocation: tier.muGenerated });
    setPaymentType('subscribe');
    setIsPaymentFormOpen(true);
  };

  // When user selects a single contribution (dollars → one-time)
  const handleSingleSelected = (tier) => {
    setPaymentTierData({ ...tier, muAllocation: tier.muGenerated });
    setPaymentType('topup');
    setIsPaymentFormOpen(true);
  };

  // After successful payment — update local profile state
  const handlePaymentSuccess = (data) => {
    if (data.user) {
      dispatch({
        type: 'AUTH_FETCH_PROFILE_SUCCESS',
        payload: {
          profile: {
            membershipTier: data.user.membershipTier,
            accountCredits: data.user.accountCredits,
            subscriptionStatus: data.user.subscriptionStatus,
            contributionAmount: data.user.contributionAmount,
            nextBillingDate: data.user.nextBillingDate,
          }
        }
      });
    }
  };

  const handleMemberHelp = () => {
    toast({
      title: "Member Help",
      description: "Please check your email for instructions on how we can assist you.",
      status: "info",
      duration: 4000,
      isClosable: true,
    });
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
        <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
          <HStack spacing={3}>
            <Icon as={FaCoins} color="yellow.400" boxSize={6} />
            <Text>Manage Your Membership</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          <VStack spacing={6} align="stretch">
            {/* Current Member Units Display */}
            <Box bg="gray.700" p={4} borderRadius="md" borderWidth="2px" borderColor="yellow.400">
              <Stat>
                <StatLabel fontSize="sm" color="gray.300">Current Member Units Balance</StatLabel>
                <StatNumber fontSize="3xl" color="yellow.400">
                  <Icon as={FaCoins} mr={2} />
                  {profile.accountCredits || 0} MU
                </StatNumber>
                <StatHelpText color="gray.400">
                  Member Units determine your priority access
                </StatHelpText>
              </Stat>
            </Box>

            {/* Current Status & Generosity Display */}
            <HStack justify="center" spacing={12} py={2}>
              <Box textAlign="center">
                <Text fontSize="md" color="gray.400" mb={2}>Status</Text>
                <Badge
                  colorScheme="green"
                  fontSize="lg"
                  px={4}
                  py={1.5}
                  borderRadius="full"
                >
                  ACTIVE
                </Badge>
              </Box>

              <Box textAlign="center">
                <Text fontSize="md" color="gray.400" mb={2}>Generosity</Text>
                <Badge
                  colorScheme={profile.membershipTier && profile.membershipTier !== 'base' ? "purple" : "gray"}
                  fontSize="lg"
                  px={4}
                  py={1.5}
                  borderRadius="full"
                >
                  {profile.membershipTier ? profile.membershipTier.toUpperCase() : "BASE"}
                </Badge>
              </Box>
            </HStack>

            <Divider borderColor="gray.600" />

            {/* Three Action Buttons */}
            <VStack spacing={4} py={2}>
              {/* Choose Your Generosity */}
              <Button
                colorScheme="purple"
                variant="outline"
                size="lg"
                height="14"
                width="full"
                fontSize="lg"
                fontWeight="semibold"
                letterSpacing="wide"
                onClick={() => setIsGenerosityModalOpen(true)}
                leftIcon={<Icon as={FaHeart} />}
                borderWidth="2px"
                _hover={{ bg: 'rgba(128, 90, 213, 0.1)', transform: 'translateY(-1px)' }}
                _active={{ bg: 'rgba(128, 90, 213, 0.2)', transform: 'translateY(0)' }}
                transition="all 0.2s"
              >
                Choose Your Generosity
              </Button>

              {/* Contribute Monthly */}
              <Button
                colorScheme="blue"
                variant="solid"
                size="lg"
                height="14"
                width="full"
                fontSize="lg"
                fontWeight="semibold"
                letterSpacing="wide"
                onClick={() => setIsMonthlyModalOpen(true)}
                leftIcon={<Icon as={FaGift} />}
                boxShadow="sm"
                _hover={{ bg: 'blue.600', transform: 'translateY(-1px)', boxShadow: 'md' }}
                _active={{ bg: 'blue.700', transform: 'translateY(0)', boxShadow: 'sm' }}
                transition="all 0.2s"
              >
                Contribute Monthly
              </Button>

              {/* Single Contribution */}
              <Button
                colorScheme="teal"
                variant="outline"
                size="lg"
                height="14"
                width="full"
                fontSize="lg"
                fontWeight="semibold"
                letterSpacing="wide"
                onClick={() => setIsSingleModalOpen(true)}
                leftIcon={<Icon as={FaCoins} />}
                borderWidth="2px"
                _hover={{ bg: 'rgba(49, 151, 149, 0.1)', transform: 'translateY(-1px)' }}
                _active={{ bg: 'rgba(49, 151, 149, 0.2)', transform: 'translateY(0)' }}
                transition="all 0.2s"
              >
                Single Contribution
              </Button>

              {/* Labor Contribution (Referral) */}
              <Button
                colorScheme="green"
                variant="outline"
                size="lg"
                height="14"
                width="full"
                fontSize="lg"
                fontWeight="semibold"
                letterSpacing="wide"
                onClick={() => setIsLaborModalOpen(true)}
                leftIcon={<Icon as={FaUsers} />}
                borderWidth="2px"
                _hover={{ bg: 'rgba(72, 187, 120, 0.1)', transform: 'translateY(-1px)' }}
                _active={{ bg: 'rgba(72, 187, 120, 0.2)', transform: 'translateY(0)' }}
                transition="all 0.2s"
              >
                Labor Contribution
              </Button>
            </VStack>

            {/* Support Actions */}
            <Box pt={2}>
              <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.400" textAlign="center">
                Need Assistance?
              </Text>
              <Button
                size="md"
                variant="ghost"
                color="blue.400"
                onClick={handleMemberHelp}
                width="full"
                borderWidth="1px"
                borderColor="blue.500"
                _hover={{ bg: 'blue.900', color: 'blue.300', borderColor: 'blue.400' }}
                _active={{ bg: 'blue.800' }}
                transition="all 0.2s"
              >
                Member Help
              </Button>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>

    {/* Modal 1: Choose Your Generosity (MU pledge) */}
    <GenerosityModal
      isOpen={isGenerosityModalOpen}
      onClose={() => setIsGenerosityModalOpen(false)}
      currentTier={profile.membershipTier || 'base'}
      onSelectTier={handleGenerositySelected}
    />

    {/* Modal 2: Monthly Contribution (dollars → MU) */}
    <MonthlyContributionModal
      isOpen={isMonthlyModalOpen}
      onClose={() => setIsMonthlyModalOpen(false)}
      onSelectTier={handleMonthlySelected}
      onSwitchToSingle={() => setIsSingleModalOpen(true)}
    />

    {/* Modal 3: Single Contribution (one-time dollars → MU) */}
    <SingleContributionModal
      isOpen={isSingleModalOpen}
      onClose={() => setIsSingleModalOpen(false)}
      onSelectTier={handleSingleSelected}
      onSwitchToRoutine={() => setIsMonthlyModalOpen(true)}
    />

    {/* Modal 4: Labor Contribution (referral program) */}
    <LaborContributionModal
      isOpen={isLaborModalOpen}
      onClose={() => setIsLaborModalOpen(false)}
      burrowerId={profile.burrowerId || profile.username}
    />

    {/* Payment Form (opened by Monthly or Single) */}
    <AcceptJSPaymentForm
      isOpen={isPaymentFormOpen}
      onClose={() => setIsPaymentFormOpen(false)}
      tierData={paymentTierData}
      paymentType={paymentType}
      onSuccess={handlePaymentSuccess}
    />
    </>
  );
};

export default MembershipModal;
